import React, { useEffect, useRef, useState } from 'react';
import '../../styles/autocomplete-select.css';

const toggleValue = (values, value) =>
  values.includes(value) ? values.filter(item => item !== value) : [...values, value];

let nextInputId = 0;

export default function AutocompleteSelect({
  label,
  options,
  value,
  onChange,
  placeholder = 'Почніть вводити назву',
  missingLabel = item => String(item),
  multiple = true,
  disabled = false,
  className = '',
  overlay = false,
  summary = false,
  searchPlaceholder,
}) {
  const id = useRef(null);
  if (!id.current) id.current = `mobile-filter-${++nextInputId}`;
  const input = useRef(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const available = [
    ...new Map(
      options.map(option => [
        option.value,
        {
          ...option,
          label: String(option.label ?? option.value),
        },
      ]),
    ).values(),
  ];
  value.forEach(item => {
    if (!available.some(option => option.value === item))
      available.push({ value: item, label: missingLabel(item) });
  });
  const filtered = available.filter(option =>
    option.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );
  const current = Math.min(active, filtered.length - 1);
  useEffect(() => {
    if (open && current >= 0)
      document
        .getElementById(`${id.current}-option-${current}`)
        ?.scrollIntoView?.({ block: 'nearest' });
  }, [open, current, search]);
  const choose = option => {
    onChange(multiple ? toggleValue(value, option.value) : [option.value]);
    setSearch('');
    setActive(0);
    if (multiple) input.current?.focus();
    else setOpen(false);
  };
  const close = () => {
    setOpen(false);
    setSearch('');
    setActive(0);
  };
  return (
    <div
      className={`as-field ${className}${overlay ? ' as-field-overlay' : ''}`}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) close();
      }}
    >
      <label htmlFor={id.current}>{label}</label>
      <div className='as-input'>
        <input
          ref={input}
          id={id.current}
          className='form-control'
          type='text'
          role='combobox'
          autoComplete='off'
          disabled={disabled}
          aria-autocomplete='list'
          aria-expanded={open}
          aria-controls={`${id.current}-list`}
          aria-activedescendant={
            open && current >= 0 ? `${id.current}-option-${current}` : undefined
          }
          placeholder={open ? searchPlaceholder || placeholder : placeholder}
          value={
            open || (multiple && !summary)
              ? search
              : summary && value.length > 1
              ? `Обрано: ${value.length}`
              : available.find(option => option.value === value[0])?.label || ''
          }
          onFocus={() => setOpen(true)}
          onChange={event => {
            setSearch(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={event => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setOpen(true);
              if (filtered.length)
                setActive(
                  open
                    ? (current + (event.key === 'ArrowDown' ? 1 : -1) + filtered.length) %
                        filtered.length
                    : 0,
                );
            } else if (event.key === 'Enter') {
              event.preventDefault();
              if (open && filtered[current]) choose(filtered[current]);
              else setOpen(true);
            } else if (event.key === 'Escape' && open) {
              event.preventDefault();
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation?.();
              close();
            }
          }}
        />
        <i className={`bi bi-${summary && !open ? 'chevron-down' : 'search'}`} aria-hidden='true' />
      </div>
      {multiple && !summary && value.length > 0 && (
        <div className='as-tags' aria-label={`Обрано: ${label}`}>
          {value.map(item => {
            const name = available.find(option => option.value === item).label;
            return (
              <span key={item} className='as-tag'>
                <span>{name}</span>
                <button
                  type='button'
                  disabled={disabled}
                  aria-label={`Прибрати ${name}`}
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => onChange(value.filter(selected => selected !== item))}
                >
                  <i className='bi bi-x' aria-hidden='true' />
                </button>
              </span>
            );
          })}
        </div>
      )}
      {open && (
        <div
          id={`${id.current}-list`}
          role='listbox'
          aria-label={label}
          aria-multiselectable={multiple}
          className='as-list'
        >
          {filtered.map((option, index) => (
            <button
              key={option.value}
              id={`${id.current}-option-${index}`}
              type='button'
              role='option'
              aria-selected={value.includes(option.value)}
              tabIndex={-1}
              className={index === current ? 'active' : ''}
              onMouseDown={event => event.preventDefault()}
              onClick={() => choose(option)}
            >
              <span>{option.label}</span>
              {value.includes(option.value) && <i className='bi bi-check2' aria-hidden='true' />}
            </button>
          ))}
          {!filtered.length && <p role='status'>Нічого не знайдено</p>}
        </div>
      )}
    </div>
  );
}
